class HeroBannerSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.hero-banner__slider-track');
    this.slides = Array.from(this.querySelectorAll('.hero-banner__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-banner__dot'));
    this.prevButton = this.querySelector('.hero-banner__arrow--prev');
    this.nextButton = this.querySelector('.hero-banner__arrow--next');
    this.socialProofEl = this.querySelector('.hero-banner__social-proof');
    this.socialProofStarsEl = this.querySelector('.hero-banner__social-proof-stars');
    this.socialProofLabelEl = this.querySelector('.hero-banner__social-proof-label');
    this.headingEl = this.querySelector('.hero-banner__heading');
    this.textEl = this.querySelector('.hero-banner__text');
    this.quoteEl = this.querySelector('.hero-banner__quote');
    this.buttonEl = this.querySelector('.hero-banner__button');

    if (!this.track || this.slides.length < 2) return;

    this.currentIndex = parseInt(this.dataset.initialIndex, 10) || 0;
    this.autoplaySpeed = parseFloat(this.dataset.autoplaySpeed);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayStopped = false;

    this.prevButton?.addEventListener('click', () => {
      this.stopAutoplay();
      this.goTo(this.currentIndex - 1);
    });
    this.nextButton?.addEventListener('click', () => {
      this.stopAutoplay();
      this.goTo(this.currentIndex + 1);
    });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(index);
      });
    });

    this.track.addEventListener('scroll', this.onScroll.bind(this));
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resumeIfAllowed());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeIfAllowed();
    });
    this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    this.addEventListener('shopify:block:deselect', () => {
      this.autoplayStopped = false;
      this.resumeIfAllowed();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resumeIfAllowed();
      }
    });

    if (this.currentIndex > 0) {
      this.goTo(this.currentIndex, { instant: true });
    }

    this.resumeIfAllowed();
  }

  hasAutoplay() {
    return !this.reducedMotion.matches && !this.autoplayStopped && Number.isFinite(this.autoplaySpeed) && this.autoplaySpeed > 0;
  }

  resumeIfAllowed() {
    if (this.hasAutoplay() && !document.hidden) this.play();
  }

  play() {
    this.pause();
    this.timer = setInterval(() => {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    }, this.autoplaySpeed * 1000);
  }

  pause() {
    clearInterval(this.timer);
  }

  stopAutoplay() {
    this.autoplayStopped = true;
    this.pause();
  }

  goTo(index, { instant = false } = {}) {
    const total = this.slides.length;
    const target = (index + total) % total;
    this.currentIndex = target;
    this.updateDots();
    this.updateContent();
    this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
  }

  onScroll() {
    clearTimeout(this.scrollSettleTimeout);
    this.scrollSettleTimeout = setTimeout(() => {
      const index = this.slides.findIndex(
        (slide) => Math.abs(slide.offsetLeft - this.track.scrollLeft) < slide.clientWidth / 2
      );
      if (index > -1 && index !== this.currentIndex) {
        this.currentIndex = index;
        this.updateDots();
        this.updateContent();
      }
    }, 100);
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  updateContent() {
    const slide = this.slides[this.currentIndex];
    if (!slide) return;
    const data = slide.dataset;

    if (this.socialProofEl) {
      const raw = data.socialProof || '';
      const hasSocialProof = Boolean(raw.trim() !== '');
      this.socialProofEl.hidden = !hasSocialProof;
      const spaceIndex = raw.indexOf(' ');
      const stars = spaceIndex > -1 ? raw.slice(0, spaceIndex) : raw;
      const label = spaceIndex > -1 ? raw.slice(spaceIndex + 1).trim() : '';
      if (this.socialProofStarsEl) this.socialProofStarsEl.textContent = stars;
      if (this.socialProofLabelEl) this.socialProofLabelEl.textContent = label;
    }

    if (this.headingEl) {
      const hasHeading = Boolean(data.heading && data.heading.trim() !== '');
      this.headingEl.hidden = !hasHeading;
      this.headingEl.innerHTML = data.headingLine2 ? `${data.heading}<br>${data.headingLine2}` : data.heading;
      this.headingEl.classList.toggle('hero-banner__heading--duco-gold', data.headingGold === 'true');
    }

    if (this.textEl) {
      const hasText = Boolean(data.text && data.text.trim() !== '');
      this.textEl.hidden = !hasText;
      this.textEl.innerHTML = data.text;
    }

    if (this.quoteEl) {
      const hasQuote = Boolean(data.quote && data.quote.trim() !== '');
      this.quoteEl.hidden = !hasQuote;
      this.quoteEl.innerHTML = data.quote;
      this.quoteEl.classList.toggle('hero-banner__quote--duco-gold', data.quoteGold === 'true');
    }

    if (this.buttonEl) {
      const hasButton = Boolean(data.buttonLabel && data.buttonLabel.trim() !== '');
      this.buttonEl.hidden = !hasButton;
      this.buttonEl.textContent = data.buttonLabel;
      this.buttonEl.classList.toggle('hero-banner__button--duco-gold', data.buttonGold === 'true');
      if (data.buttonLink) {
        this.buttonEl.setAttribute('href', data.buttonLink);
        this.buttonEl.removeAttribute('role');
        this.buttonEl.removeAttribute('aria-disabled');
      } else {
        this.buttonEl.removeAttribute('href');
        this.buttonEl.setAttribute('role', 'link');
        this.buttonEl.setAttribute('aria-disabled', 'true');
      }
    }
  }

  onBlockSelect(event) {
    const slide = event.target.closest('.hero-banner__slide');
    if (!slide) return;
    const index = this.slides.indexOf(slide);
    if (index === -1) return;
    this.stopAutoplay();
    this.goTo(index, { instant: true });
  }
}

customElements.define('hero-banner-slider', HeroBannerSlider);
